import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  signal,
  inject,
  DestroyRef,
  Inject,
} from '@angular/core';

import { LngLat, Map, Marker } from 'mapbox-gl';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Country } from '../../../models';

import { Business } from '../../../models';

import {
  BuildingService,
  CountryService,
  BusinessService,
} from '../../../services';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { MapModalComponent } from '../map-modal';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ToastAlertService } from '@core/services';
import { environment } from '@environments/environment';
import { ButtonComponent } from '@shared/components/button/button.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TextAreaComponent } from '@shared/components/text-area/text-area.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { InputComponent } from '@shared/components/input/input.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { TitleComponent } from '@shared/components/title/title.component';
import { MatCardModule } from '@angular/material/card';

@Component({
  selector: 'building-modal',
  templateUrl: './building-modal.component.html',
  styles: [
    `
      #map {
        width: 100%;
        height: 150px;
        margin-bottom: 3%;
        cursor: pointer;
        border-radius: 10px;
      }
    `,
  ],
  standalone: true,
  imports: [
    MatCardModule,
    TitleComponent,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    ReactiveFormsModule,
    MatDialogContent,
    InputComponent,
    SelectComponent,
    TextAreaComponent,
    MatFormFieldModule,
    MatDialogActions,
    ButtonComponent,
    MatDialogClose,
  ],
})
export class BuildingModalComponent implements AfterViewInit {
  public buildingForm!: FormGroup;
  public countries = signal<Country[]>([]);
  public business = signal<Business[]>([]);

  // Map environments
  @ViewChild('map')
  private divMap?: ElementRef;

  private zoom = signal<number>(13);
  private map = signal<Map | null>(null);
  private currentLngLat = signal<LngLat>(
    new LngLat(-90.5132588267888, 14.615485437580773)
  );
  private marker = signal<Marker | null>(null);

  private countryService = inject(CountryService);
  private buildingService = inject(BuildingService);
  private _business = inject(BusinessService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<BuildingModalComponent>);
  private formBuilder = inject(FormBuilder);
  private alertService = inject(ToastAlertService);

  constructor(@Inject(MAT_DIALOG_DATA) public data: any) {
    this.getAllBusiness();
    this.getCountries();
    this.buildForm();
  }

  async ngAfterViewInit() {
    if (!this.divMap) throw 'Elemento HTML no encontrado';

    if (this.data && this.data.longitude && this.data.latitude) {
      const lngLat = new LngLat(this.data.longitude, this.data.latitude);
      this.currentLngLat.set(lngLat);
    }

    this.map.set(
      new Map({
        accessToken: environment.mapbox.accessToken,
        container: this.divMap.nativeElement,
        style: 'mapbox://styles/mapbox/dark-v10',
        center: this.currentLngLat(),
        zoom: this.zoom(),
        interactive: false,
      })
    );

    this.marker.set(
      new Marker({ color: 'red' })
        .setLngLat(this.currentLngLat())
        .addTo(this.map()!)
    );
  }

  get id_building() {
    return this.buildingForm.get('id');
  }

  public openMapModal() {
    const dialogActionDetail = this.dialog.open(MapModalComponent, {
      width: '100vh',
      height: '80vh',
      data: {
        lngLat: this.currentLngLat(),
      },
      disableClose: true,
    });
    dialogActionDetail
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.updateMarker(result);
      });
  }

  public onSubmit(): void {
    if (this.buildingForm.valid) {
      if (this.data && typeof this.data != 'undefined' && this.data.toEdit) {
        this.buildingService
          .updateBuilding(
            this.buildingForm.value,
            Number(this.id_building?.value)
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: resp => {
              this.dialogRef.close(true);
              if (resp.code === 10000) {
                this.alertService.sucess(`Edificio modificado con éxito`);
              } else {
                this.alertService.error(resp.detail);
              }
            },
            error: error => {
              this.alertService.error(`Error: ${error}`);
            },
          });
      } else {
        this.buildingService
          .storeBuilding(this.buildingForm.value)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: ({ code, detail }) => {
              this.dialogRef.close(true);
              if (code === 10000) {
                this.alertService.sucess('Edificio creado con éxito');
              } else {
                this.alertService.error(detail);
              }
            },
            error: error => {
              this.alertService.error(`Error: ${error}`);
            },
          });
      }
    }
  }

  private updateMarker(result: LngLat) {
    this.marker.update(marker => marker!.setLngLat(result));
    this.map.update(map => map!.setCenter(result));
    this.currentLngLat.set(result);
    this.buildingForm.patchValue({
      latitude: result.lat,
      longitude: result.lng,
    });
  }

  private getCountries(): void {
    this.countryService
      .getCountrys()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: resp => {
          if (resp.code === 10000) this.countries.set(resp.reply);
          else {
            this.alertService.error(resp.detail);
          }
        },
        error: error => {
          console.log(error);
          this.alertService.error(`Error: ${error}`);
        },
      });
  }

  private getAllBusiness(): void {
    this._business
      .getBusiness()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: any) => {
          if (resp.code === 10000) {
            if (this.data.idBusiness) {
              const filteredResponse = resp.reply.filter(
                (item: any) => item.idBusiness == this.data.idBusiness
              );
              this.business.set(filteredResponse);
            } else {
              this.business.set(resp.reply);
            }
          } else {
            this.alertService.error(resp.detail);
          }
        },
        error: error => {
          console.log(error);
          this.alertService.error(`Error: ${error}`);
        },
      });
  }

  private buildForm(): void {
    if (this.data && typeof this.data != 'undefined' && this.data.toEdit) {
      this.buildingForm = this.formBuilder.group({
        id: [this.data.buildingid],
        name: [this.data.name, [Validators.required, Validators.maxLength(50)]],
        code: [this.data.code, [Validators.required, Validators.maxLength(20)]],
        address: [
          this.data.address,
          [
            Validators.required,
            Validators.maxLength(256),
            Validators.minLength(5),
          ],
        ],
        idBusiness: [parseInt(this.data.idBusiness, 10), Validators.required],
        latitude: [this.data.latitude],
        longitude: [this.data.longitude],
      });
    } else {
      this.buildingForm = this.formBuilder.group({
        id: ['0'],
        name: ['', [Validators.required, Validators.maxLength(50)]],
        code: ['', [Validators.required, Validators.maxLength(20)]],
        address: [
          '',
          [
            Validators.required,
            Validators.maxLength(256),
            Validators.minLength(5),
          ],
        ],
        idBusiness: [this.data.idBusiness, Validators.required],
        latitude: [this.currentLngLat().lat],
        longitude: [this.currentLngLat().lng],
      });
    }
  }

  clearForm() {
    this.buildForm();
  }
}
